/**
 * quote_public_view_controller.js — online quote page action surfacing (Stimulus).
 *
 * Element-scoped controller for templates/quote_public_view.html. When the email
 * link carries ?action=accept|decline, it surfaces the matching action: scrolls
 * the controller element into view, pulses the matching button, and focuses the
 * optional message box. Display nicety only — the accept/decline itself is the
 * form POST and works fully without this controller (and without JS at all, so
 * link scanners can't trigger a mutation).
 *
 * Wiring (templates/quote_public_view.html):
 *   data-controller="quote-public-view"     on the #quote-actions card
 *   data-quote-public-view-target="acceptButton" / "declineButton" / "message"
 *
 * Lifecycle: connect() reads the URL param and surfaces once; disconnect() and a
 * connect-scoped turbo:before-cache strip the injected pulse class + clear the
 * timer so the cached snapshot matches the server render.
 */

import { Controller } from "@hotwired/stimulus";

    var PULSE_MS = 2400;

export default class extends Controller {
        static get targets() {
            return ['acceptButton', 'declineButton', 'message', 'messageToggle', 'messageWrap'];
        }

        connect() {
            this._onBeforeCache = () => this._revert();
            document.addEventListener('turbo:before-cache', this._onBeforeCache);
            this._surface();
        }

        disconnect() {
            if (this._onBeforeCache) {
                document.removeEventListener('turbo:before-cache', this._onBeforeCache);
                this._onBeforeCache = null;
            }
            this._revert();
        }

        _surface() {
            var action = new URLSearchParams(window.location.search).get('action');
            if (action !== 'accept' && action !== 'decline') return;

            this.element.scrollIntoView({ behavior: 'smooth', block: 'center' });

            var btn = action === 'accept'
                ? (this.hasAcceptButtonTarget ? this.acceptButtonTarget : null)
                : (this.hasDeclineButtonTarget ? this.declineButtonTarget : null);
            if (btn) {
                this._pulsed = btn;
                btn.classList.add('quote-btn-pulse');
                this._pulseTimer = setTimeout(() => {
                    btn.classList.remove('quote-btn-pulse');
                    this._pulsed = null;
                }, PULSE_MS);
            }

            // Only focus the message box if it's already open (it starts
            // collapsed). The ?action highlight is about the button, not the note.
            if (this.hasMessageTarget && this.hasMessageWrapTarget && !this.messageWrapTarget.hidden) {
                try { this.messageTarget.focus({ preventScroll: true }); }
                catch { this.messageTarget.focus(); }
            }
        }

        /** Reveal the optional message box and focus it (progressive disclosure). */
        toggleMessage() {
            if (!this.hasMessageWrapTarget) return;
            this.messageWrapTarget.hidden = false;
            if (this.hasMessageToggleTarget) {
                this.messageToggleTarget.hidden = true;
                this.messageToggleTarget.setAttribute('aria-expanded', 'true');
            }
            if (this.hasMessageTarget) this.messageTarget.focus();
        }

        // Restore the card to its server-rendered state for a clean Turbo snapshot.
        _revert() {
            clearTimeout(this._pulseTimer);
            if (this._pulsed) {
                this._pulsed.classList.remove('quote-btn-pulse');
                this._pulsed = null;
            }
        }
}
